// Dragonlord helpers. Pure. No React, no Three.js.
//
// ── Codex (Dragonlord) ──
//   "Before setup, set aside a Unique Dragon. Once on your turn, if
//    you meet its threshold, you may pay (3) to gain its abilities
//    this turn."
//
// The set-aside is a Collection pick (mirrors the Imposter mask UX);
// the invoke borrows the imposterMask identity machinery to wear the
// Dragon's rules identity for the turn while keeping Dragonlord's
// face. This module owns the read-only predicates and the threshold
// math.
//
// Catalog shape is the NORMALIZED card object (type / rarity /
// traits / *Threshold top-level), same contract as the other avatar
// helper modules.

import { countsAsMinion } from '../../../core/typeline';

export const DRAGONLORD_AVATAR_ID = 'sorcery-dragonlord';

function lowerTraits(fullCard) {
  const raw = fullCard?.traits ?? fullCard?.subTypes;
  return (Array.isArray(raw) ? raw : [raw])
    .filter(Boolean)
    .flatMap((s) => String(s).toLowerCase().split(/[,;\s]+/))
    .filter(Boolean);
}

/** True iff the catalog card is a Unique Dragon (the legal set-aside).
 *  countsAsMinion so Automaton Dragons (Kairos the Archivist. An
 *  artifact that is a minion per codex) qualify too. */
export function isUniqueDragon(fullCard) {
  if (!countsAsMinion(fullCard)) return false;
  if (String(fullCard.rarity || '').toLowerCase() !== 'unique') return false;
  return lowerTraits(fullCard).includes('dragon');
}

/** True iff any card in the pile resolves to a Unique Dragon. */
export function pileHasUniqueDragon(pileCards, catalog) {
  return (pileCards || []).some((c) =>
    isUniqueDragon(catalog?.find((f) => f?.unique_id === c.cardId)));
}

/**
 * Every Unique Dragon the player owns on their ACCOUNT. The legal
 * set-aside pool is the player's whole card collection, not the
 * in-game Collection pile or the loaded deck. `collection` is the
 * arena profile's rows ({ cardId, foiling, quantity }); returns the
 * matching catalog cards, deduped by unique_id.
 */
export function ownedUniqueDragons(collection, catalog) {
  if (!Array.isArray(collection) || !Array.isArray(catalog)) return [];
  const out = [];
  const seen = new Set();
  for (const row of collection) {
    if (!row?.cardId || seen.has(row.cardId)) continue;
    if ((row.quantity ?? 1) <= 0) continue;
    const full = catalog.find((c) => c?.unique_id === row.cardId);
    if (!isUniqueDragon(full)) continue;
    seen.add(row.cardId);
    out.push(full);
  }
  return out;
}

/**
 * Every Unique Dragon that exists in the game, deduped by unique_id.
 * Solo and sandbox matches ignore account ownership (any card may be
 * played), so the set-aside pool is the full catalog rather than the
 * player's collection.
 */
export function allUniqueDragons(catalog) {
  if (!Array.isArray(catalog)) return [];
  const out = [];
  const seen = new Set();
  for (const full of catalog) {
    if (!full?.unique_id || seen.has(full.unique_id)) continue;
    if (!isUniqueDragon(full)) continue;
    seen.add(full.unique_id);
    out.push(full);
  }
  return out;
}

/**
 * True when the match ignores account ownership, so every Unique
 * Dragon in the game is a legal set-aside (mirrors the deck builder's
 * `skipOwnership`). Three sandbox flavours qualify: the local
 * single-player "Solo" sandbox (`isCasualSandbox`), the online
 * matchmade Sandbox queue (`isSandboxMatch`), and public / friends
 * Sandbox instances (`instanceMode === 'sandbox'`). Ranked and
 * constructed matches gate to the player's collection.
 */
export function matchIgnoresOwnership({ isCasualSandbox, isSandboxMatch, instanceMode } = {}) {
  return !!(isCasualSandbox || isSandboxMatch || instanceMode === 'sandbox');
}

/**
 * The legal Dragonlord set-aside pool for the current match: every
 * Unique Dragon in the game when ownership is ignored, otherwise only
 * the Unique Dragons in the player's account collection.
 */
export function dragonSetAsidePool({ collection, catalog, isCasualSandbox, isSandboxMatch, instanceMode } = {}) {
  return matchIgnoresOwnership({ isCasualSandbox, isSandboxMatch, instanceMode })
    ? allUniqueDragons(catalog)
    : ownedUniqueDragons(collection, catalog);
}

/**
 * True while the Dragonlord is currently wearing its companion's
 * rules identity. The invoking client stamps `_dragonIdentitySnapshot`
 * locally; the opponent's client stamps `_dragonlordBorrow` when the
 * `avatar:mask` broadcast carries the dragonlord flag. One predicate
 * for both sides so the inspector panel and the battlefield echo can
 * never disagree about the merged state.
 */
export function isDragonIdentityActive(cardInstance) {
  return !!(cardInstance?._dragonIdentitySnapshot
    || (cardInstance?._dragonlordBorrow && cardInstance?._originalIdentity));
}

/**
 * "If you meet its threshold." Every element threshold printed on
 * the Dragon must be covered by the player's current affinity
 * snapshot ({ air, earth, fire, water }).
 */
export function meetsDragonThreshold(snapshot, fullCard) {
  if (!fullCard) return false;
  const have = snapshot || {};
  return (fullCard.airThreshold || 0) <= (have.air || 0)
    && (fullCard.earthThreshold || 0) <= (have.earth || 0)
    && (fullCard.fireThreshold || 0) <= (have.fire || 0)
    && (fullCard.waterThreshold || 0) <= (have.water || 0);
}
